import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import adminAxios from "../admin/adminAxios"; 
import { 
  LayoutDashboard, 
  Users, 
  Settings, 
  MonitorPlay, 
  Wallet, 
  MessageSquare, 
  LogOut,
  ShieldCheck,
  ClipboardCheck, 
  Landmark, 
  Bot,
  Package 
} from "lucide-react";

const colors = {
  primary: "#03f8d7", 
  accent: "#ff2d75",
  deepBg: "#060608",
  surface: "rgba(13, 13, 18, 0.95)",
  textMain: "#ffffff",
  textMuted: "#64748b",
  border: "rgba(3, 248, 215, 0.15)",
};

function AdminSidebar({ isOpen }) {
  const location = useLocation();
  const { logout, socket } = useAuth();
  const [totalUnread, setTotalUnread] = useState(0);

  // Retrieve isolated tenant context identifier
  const tenantId = localStorage.getItem("adminId") || "global";
  
  const notificationSound = useRef(new Audio("/notify.mp3"));

  const fetchTotalUnread = useCallback(async () => {
    try {
      const { data } = await adminAxios.get("/chat/admin/unread-count", { params: { tenantId } });
      setTotalUnread(data.count || 0);
    } catch (err) {
      console.warn("Sidebar unread sync paused.");
    }
  }, [tenantId]);

  useEffect(() => {
    fetchTotalUnread();

    if (socket) {
      const joinTenantAdmin = () => {
        socket.emit("join", { userId: "admin", role: "admin", tenantId });
      };

      if (socket.connected) joinTenantAdmin();
      socket.on("connect", joinTenantAdmin);

      const handleNewMessage = (msg) => {
        if (msg.tenantId && msg.tenantId !== tenantId) return;
        if (location.pathname !== "/admin/chat" && msg.senderRole === "user") {
          setTotalUnread((prev) => prev + 1);
          notificationSound.current.play().catch(e => console.warn("Audio play blocked."));
        }
      };

      const handleUnreadUpdate = (data) => {
        if (data.tenantId && data.tenantId !== tenantId) return;
        if (data.totalAdminUnread !== undefined) {
          setTotalUnread(data.totalAdminUnread);
        } else {
          fetchTotalUnread();
        }
      };

      socket.on("newMessage", handleNewMessage);
      socket.on("unreadUpdate", handleUnreadUpdate);

      return () => {
        socket.off("connect", joinTenantAdmin);
        socket.off("newMessage", handleNewMessage);
        socket.off("unreadUpdate", handleUnreadUpdate);
      };
    }
  }, [socket, location.pathname, fetchTotalUnread, tenantId]);

  useEffect(() => {
    if (location.pathname === "/admin/chat") {
      setTotalUnread(0);
    }
  }, [location.pathname]);

  const menu = [
    { label: "OPERATIONS HUB", path: "/admin/dashboard", icon: <LayoutDashboard size={18} /> },
    { label: "MANAGE USERS", path: "/admin/users", icon: <Users size={18} /> },
    { label: "MANAGE ORDERS", path: "/admin/orders", icon: <Package size={18} /> }, 
    { label: "ASSIGN & UPLOAD PRODUCTS", path: "/admin/assign", icon: <Settings size={18} /> },
    { label: "BANNERS", path: "/admin/banners", icon: <MonitorPlay size={18} /> },
    { label: "FINANCE MGMT", path: "/admin/finance", icon: <Landmark size={18} /> },
    { label: "DEPOSIT MANAGEMENT", path: "/admin/payment-audit", icon: <ClipboardCheck size={18} /> },
    { label: "WITHDRAW MANAGEMENT", path: "/admin/withdraw", icon: <Wallet size={18} /> },
    { 
      label: "GLOBAL CHAT", 
      path: "/admin/chat", 
      icon: <MessageSquare size={18} />,
      badge: totalUnread 
    },
    { label: "ROBOT COMMANDS", path: "/admin/auto-replies", icon: <Bot size={18} /> },
  ];

  const sidebarStyle = {
    width: "260px",
    height: "100vh",
    background: colors.surface,
    backdropFilter: "blur(12px)",
    position: "fixed",
    left: 0,
    transform: isOpen ? "translateX(0)" : "translateX(-100%)",
    top: 0,
    zIndex: 9999,
    transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
    display: "flex",
    flexDirection: "column",
    padding: "30px 24px",
    boxSizing: "border-box",
    borderRight: `1px solid ${colors.border}`,
    boxShadow: isOpen ? "25px 0 60px rgba(0,0,0,0.9)" : "none",
    visibility: isOpen ? "visible" : "hidden",
  };

  const navItemStyle = (isActive) => ({
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "12px 20px",
    textDecoration: "none",
    fontSize: "10px", 
    fontWeight: "700",
    letterSpacing: "1.8px",
    color: isActive ? colors.primary : colors.textMuted,
    background: isActive ? "rgba(3, 248, 215, 0.08)" : "transparent",
    borderRadius: "12px",
    border: isActive ? `1px solid ${colors.border}` : "1px solid transparent",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    marginBottom: "6px",
    position: "relative",
  });

  return (
    <div style={sidebarStyle} data-tenant-scope={tenantId}>
      <style>{`
        @keyframes blink {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.9); }
          100% { opacity: 1; transform: scale(1); }
        }
        .uplink-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: ${colors.primary};
          box-shadow: 0 0 10px ${colors.primary};
          animation: blink 2s infinite ease-in-out;
        }
        .uplink-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 10px 15px;
          border-radius: 12px;
          border: 1px solid ${colors.border};
          background: rgba(0, 0, 0, 0.3);
          margin-top: 10px;
        }
      `}</style>

      <div style={{ textAlign: "left", marginBottom: "30px", paddingLeft: "10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
          <ShieldCheck color={colors.primary} size={24} />
          <h2 style={{ color: "#fff", letterSpacing: "3px", fontSize: "1.1rem", fontWeight: "900", margin: 0 }}>
            CORE <span style={{ color: colors.primary }}>ADMIN</span>
          </h2>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: colors.primary, boxShadow: `0 0 8px ${colors.primary}` }} />
          <p style={{ color: colors.textMuted, fontSize: "9px", margin: 0, fontWeight: "600", letterSpacing: "1px" }}>
            V3.0.4
          </p>
        </div>
      </div>
      
      <nav style={{ flex: 1, overflowY: "auto", paddingRight: "4px" }}>
        {menu.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              style={navItemStyle(isActive)}
              onMouseOver={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = "#fff";
                  e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                }
              }}
              onMouseOut={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = colors.textMuted;
                  e.currentTarget.style.background = "transparent";
                }
              }}
            >
              <span style={{ 
                color: isActive ? colors.primary : colors.textMuted,
                transition: "0.3s",
                filter: isActive ? `drop-shadow(0 0 5px ${colors.primary})` : "none"
              }}>
                {item.icon}
              </span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.badge > 0 && (
                <span style={{ background: colors.accent, color: "#fff", fontSize: "9px", padding: "2px 6px", borderRadius: "6px", fontWeight: "900" }}>
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div style={{ marginTop: "15px", borderTop: `1px solid ${colors.border}`, paddingTop: "20px" }}>
        <button 
          onClick={logout} 
          style={{ 
            width: "100%", 
            background: "rgba(255, 45, 117, 0.05)", 
            border: "1px solid rgba(255, 45, 117, 0.2)", 
            color: "#ff2d75", 
            padding: "14px", 
            cursor: "pointer", 
            borderRadius: "12px",
            fontSize: "10px",
            fontWeight: "800",
            letterSpacing: "2.5px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            transition: "0.2s"
          }}
          onMouseOver={(e) => e.currentTarget.style.background = "rgba(255, 45, 117, 0.12)"}
          onMouseOut={(e) => e.currentTarget.style.background = "rgba(255, 45, 117, 0.05)"}
        >
          <LogOut size={16} />
          LOGOUT SESSION
        </button>

        {/* UPLINK ACTIVE STATUS BADGE */}
        <div className="uplink-badge">
          <div className="uplink-dot"></div>
          <div className="uplink-dot"></div>
          <div className="uplink-dot"></div>
          <span style={{ 
            color: colors.primary, 
            fontSize: "9px", 
            fontWeight: "900", 
            letterSpacing: "1.5px" 
          }}>
            UPLINK ACTIVE 
          </span>
        </div>
      </div>
    </div>
  );
}

export default AdminSidebar;